import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Loader2, Lock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageMessage } from "@/components/page-message";
import { useOrganization } from "@/hooks/use-organization";
import { Permission, useRolePermissions } from "@/hooks/use-role-permissions";
import { useTRPC } from "@/trpc";
import {
  exclusiveEndToLastDay,
  formatCalendarDate,
  getTodayInTimezone,
} from "../utils/wage-dates";
import { getAssigneeName } from "../utils/assignee-name";
import { AssignOrganizationWageSetDialog } from "./assign-organization-wage-set-dialog";
import { ChangeWagesDialog } from "./change-wages-dialog";
import { EndAssignmentDialog } from "./end-assignment-dialog";
import { RetireOrganizationWageSetDialog } from "./retire-organization-wage-set-dialog";
import {
  AssignmentsList,
  type AssignmentRow,
} from "./organization-wage-set-detail/assignments-list";
import { VersionHistory } from "./organization-wage-set-detail/version-history";

type OrganizationWageSetDetailPageProps = {
  organizationWageSetId: string;
  user: { is_admin: boolean } | null;
};

enum OpenDialog {
  None = "none",
  ChangeWages = "change-wages",
  Retire = "retire",
  Assign = "assign",
}

export function OrganizationWageSetDetailPage({
  organizationWageSetId,
  user,
}: OrganizationWageSetDetailPageProps) {
  const trpc = useTRPC();
  const { selectedOrgBusinessUnit } = useOrganization();
  const { hasPermission } = useRolePermissions(user);
  const canView = hasPermission(Permission.ViewJobOffers);
  const canManage = hasPermission(Permission.ManageJobOffers);
  const [openDialog, setOpenDialog] = useState(OpenDialog.None);
  const [endingAssignment, setEndingAssignment] =
    useState<AssignmentRow | null>(null);
  const timezoneId = selectedOrgBusinessUnit.timezoneId;

  const detailQuery = useQuery(
    trpc.organizationWageSet.get.queryOptions(
      {
        organizationBusinessUnitId: selectedOrgBusinessUnit.id,
        organizationWageSetId,
      },
      { enabled: canView },
    ),
  );

  if (!canView) {
    return (
      <PageMessage
        icon={Lock}
        title="No access to wage sets"
        description="You don't have permission to view wage sets for this business unit."
      />
    );
  }

  const detail = detailQuery.data;
  const openVersion = detail?.versions.find(
    (row) => row.version.effectiveEndsAt === null,
  );
  const isRetired = detail?.orgWageSet.retiredAt != null;
  const canChange = canManage && !isRetired && openVersion !== undefined;
  const closeDialog = () => setOpenDialog(OpenDialog.None);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-4xl space-y-4 px-3 py-4 sm:space-y-6 sm:px-4 sm:py-8 lg:px-8">
        <Button asChild variant="ghost" size="sm" className="-ml-2">
          <Link
            to="/org/bu/$organizationBusinessUnitId/jobs/wage-sets"
            params={{
              organizationBusinessUnitId: selectedOrgBusinessUnit.id,
            }}
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            Wage sets
          </Link>
        </Button>

        {detailQuery.isLoading && (
          <div className="flex justify-center py-12">
            <Loader2
              aria-label="Loading wage set"
              className="h-8 w-8 animate-spin text-blue-600"
            />
          </div>
        )}

        {detailQuery.error && (
          <PageMessage
            icon={Lock}
            title="Couldn't load this wage set"
            description={detailQuery.error.message}
          >
            <Button variant="outline" onClick={() => detailQuery.refetch()}>
              Try again
            </Button>
          </PageMessage>
        )}

        {detail && (
          <>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-3xl font-bold text-slate-900">
                    {detail.orgWageSet.name}
                  </h1>
                  {detail.orgWageSet.retiredAt && (
                    <Badge variant="secondary">
                      Retired, last day{" "}
                      {formatCalendarDate(
                        exclusiveEndToLastDay({
                          instant: detail.orgWageSet.retiredAt,
                          timezoneId,
                        }),
                      )}
                    </Badge>
                  )}
                </div>
              </div>
              {canManage && (
                <div className="flex flex-wrap gap-2">
                  {!isRetired && (
                    <Button onClick={() => setOpenDialog(OpenDialog.Assign)}>
                      Assign
                    </Button>
                  )}
                  {canChange && (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => setOpenDialog(OpenDialog.ChangeWages)}
                      >
                        Change wages
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setOpenDialog(OpenDialog.Retire)}
                      >
                        Retire
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>

            <VersionHistory
              versions={detail.versions}
              retiredAt={detail.orgWageSet.retiredAt}
              timezoneId={timezoneId}
              today={getTodayInTimezone({ timezoneId })}
            />
            <AssignmentsList
              assignments={detail.assignments}
              timezoneId={timezoneId}
              canManage={canManage}
              onEnd={setEndingAssignment}
            />

            {openDialog === OpenDialog.Assign && (
              <AssignOrganizationWageSetDialog
                open
                onOpenChange={closeDialog}
                organizationWageSetId={detail.orgWageSet.id}
              />
            )}
            {openDialog === OpenDialog.ChangeWages && openVersion && (
              <ChangeWagesDialog
                open
                onOpenChange={closeDialog}
                organizationWageSetId={detail.orgWageSet.id}
                currentVersionId={openVersion.version.id}
                currentWages={openVersion.wages}
              />
            )}
            {openDialog === OpenDialog.Retire && openVersion && (
              <RetireOrganizationWageSetDialog
                open
                onOpenChange={closeDialog}
                organizationWageSetId={detail.orgWageSet.id}
                organizationWageSetName={detail.orgWageSet.name}
                currentVersionId={openVersion.version.id}
              />
            )}
            {endingAssignment && (
              <EndAssignmentDialog
                open
                onOpenChange={() => setEndingAssignment(null)}
                assignmentId={endingAssignment.assignment.id}
                assigneeName={getAssigneeName(endingAssignment)}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
